<template>
  <div>
    <div class="AnimationViewerWrapper | relative inline-block">
      <img
        :class="{ invisible: autoPlay !== true }"
        :src="file.previews['1000x']"
        @click="stop()"
        alt=""
        class="cursor-pointer"
      />

      <div
        v-if="autoPlay === false && !isLoading"
        @mousedown="mouseDown"
        @touchstart="mouseDown"
        @mouseup="mouseUp"
        @touchend="mouseUp"
        @mouseleave="mouseLeave"
        @mousemove="mouseMove"
        @touchmove="mouseMove"
        class="AnimationViewerSequences select-none"
        style="cursor: ew-resize"
      >
        <div
          v-for="(frame, frameIndex) in info.frames"
          :key="frame"
          :style="{
            height: '100%',
            backgroundImage: 'url(' + frame + ')',
            backgroundSize: zoom + '%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
          }"
          :class="{ 'opacity-0': frameIndex !== index }"
        />

        <div
          class="absolute bottom-0 left-0 right-0 flex justify-center items-center space-x-5 select-none bg-black bg-opacity-30 px-2 py-2 rounded-xl m-2"
        >
          <div @click.stop.prevent="prevImage()" class="cursor-pointer">
            <aw-icon
              icon="nextsimple"
              class="w-5 h-5 transform rotate-180 text-white hover:text-bc-blue"
            />
          </div>
          <div @click.stop.prevent="play()" v-if="autoPlay === false" class="cursor-pointer">
            <aw-icon
              icon="play"
              class="w-5 h-5 text-white hover:text-bc-blue"
            />
          </div>
          <div @click.stop.prevent="stop()" v-else class="cursor-pointer">
            <aw-icon icon="pause" class="w-5 h-5 " />
          </div>
          <div @click.stop.prevent="nextImage()" class="cursor-pointer">
            <aw-icon
              icon="nextsimple"
              class="w-5 h-5 text-white hover:text-bc-blue"
            />
          </div>
          <div @click.stop.prevent="zoomIn()" :class="{  'cursor-pointer': canZoomIn }">
            <aw-icon
              icon="zoomin"
              class="w-5 h-5"
              :class="{ 'text-white': canZoomIn, 'text-gray-400': !canZoomIn, 'hover:text-bc-blue': canZoomIn }"
            />
          </div>
          <div @click.stop.prevent="zoomOut()" :class="{  'cursor-pointer': canZoomOut }">
            <aw-icon
              icon="zoomout"
              class="w-5 h-5"
              :class="{ 'text-white': canZoomOut, 'text-gray-400': !canZoomOut, 'hover:text-bc-blue': canZoomOut }"
            />
          </div>
        </div>
      </div>
      <div v-if="autoPlay === false && isLoading" class="AnimationViewerSequences flex items-center justify-center">
        <div class="flex items-center justify-center space-x-4">
          <aw-icon icon="loading" class="animate-spin w-5 h-5  " />
          <span v-if="info === null">Loading ...</span>
          <span v-else>{{ loaded }} / {{ info.frames.length }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios'

const defaults = {
  info: null,
  loaded: 0,
  error: false,
  autoPlay: true,
  index: 0,
  isMouseDown: false,
  mouseLastX: 0,
  direction: 0,
  timeInterval: 0,
  lastTimeStamp: 0,
  momentumId: null,
  timeoutId: null,
  zoom: 100,
}

export default {
  props: {
    file: {
      type: Object
    }
  },
  data() {
    return { ...defaults }
  },
  computed: {
    isLoading() {
      return !this.info || this.info?.frames.length > this.loaded
    },
    canZoomIn() {
      return this.zoom < 200
    },
    canZoomOut() {
      return this.zoom > 100
    },
  },
  methods: {
    stop() {
      this.autoPlay = false
      if (this.info === null) {
        this.loadInfo()
      }
    },
    play() {
      this.autoPlay = true
    },
    prevImage() {
      this.index =
        this.index === 0 ? this.info.frames.length - 1 : this.index - 1
    },
    nextImage() {
      this.index =
        this.index + 1 === this.info.frames.length ? 0 : this.index + 1
    },
    async loadInfo() {
      const infoResponse = await axios.get(this.file.previews.info)
      this.info = infoResponse.data
      this.loadImages()
    },
    loadImages() {
      this.info.frames.forEach(this.createLoader)
    },
    createLoader(frame) {
      const img = new Image()
      img.onload = () => {
        this.handleLoad(img)
      }
      img.onerror = () => {
        this.handleError(img)
      }
      img.src = frame
    },
    destroyLoader(img) {
      img.onload = null
      img.onerror = null
      img = null
    },
    handleLoad(img) {
      this.destroyLoader(img)
      this.loaded++
    },
    handleError(img) {
      this.destroyLoader(img)
      this.error = true
    },
    mousePlay() {
      clearTimeout(this.timeoutId)
      if (this.direction === 0) {
        return
      }

      if (this.direction === 1) {
        this.nextImage()

      } else {
        this.prevImage()
      }

      if (this.timeInterval < 50) {
        this.timeoutId = setTimeout(this.mousePlay, this.timeInterval)
        this.timeInterval *= 1 + (this.timeInterval / 400)
      
      } else {
        this.direction = 0
        clearTimeout(this.timeoutId)
      }
    },
    mouseDown(e) {
      if (this.autoPlay) return

      this.isMouseDown = true
      this.mouseLastX = e.pageX || e.touches[0].pageX
      this.lastTimeStamp = e.timeStamp
      this.timeInterval = 0
      clearTimeout(this.timeoutId)
    },
    mouseLeave() {
      if (this.autoPlay) return

      this.isMouseDown = false
    },
    mouseUp() {
      if (this.autoPlay) return

      this.isMouseDown = false
    },
    mouseMove(e) {
      if (this.autoPlay) return
      if (!this.isMouseDown) return
      e.preventDefault()

      const diffX = (e.pageX || e.touches[0].pageX) - this.mouseLastX

      if (diffX === 0) {
        this.direction = 0

      } else if (Math.abs(diffX) < 3) {
        return
      }

      this.timeInterval = e.timeStamp - this.lastTimeStamp
      this.mouseLastX = e.pageX || e.touches[0].pageX
      this.lastTimeStamp = e.timeStamp

      if (diffX > 0) {
        this.direction = 1
        this.mousePlay()
      
      } else if (diffX < 0) {
        this.direction = -1
        this.mousePlay()
      }
    },
    zoomIn() {
      if (!this.canZoomIn) return

      this.zoom += 25
    },
    zoomOut() {
      if (!this.canZoomOut) return
      
      this.zoom -= 25
    }
  },
  watch: {
    file() {
      Object.keys(defaults).forEach(k => { this[k] = defaults[k] })
    }
  }
}
</script>

<style lang="scss" scoped>
.AnimationViewerSequences {
  @apply absolute top-0 bottom-0 right-0 left-0;
}
</style>
